import React from 'react';

import { Badge, type BadgeStatus } from '@/design-system/badge';
import { ProgressBar } from '@/design-system/progress-bar';

import {
  fmtPct,
  OCCUPANCY_STATUS,
  pctTone,
  ZONE_OCCUPANCY_STATUS,
} from '../occupancy';
import type {
  StorageOccupancyStatus,
  UtilizationBasis,
  ZoneOccupancyStatus,
  ZoneOccupancyThresholds,
} from '../types';

/** `OCCUPANCY_STATUS` tone → kit Badge status. */
const BADGE_STATUS: Record<
  (typeof OCCUPANCY_STATUS)[StorageOccupancyStatus]['tone'],
  BadgeStatus
> = {
  neutral: 'faded',
  info: 'information',
  warning: 'warning',
  danger: 'error',
  success: 'success',
};

/** Status pill of a bin / roll-up (Tersedia … Over kapasitas, Kapasitas belum diatur). */
export const OccupancyBadge: React.FC<{
  status: StorageOccupancyStatus | null | undefined;
  testId?: string;
}> = ({ status, testId }) => {
  if (!status) return <span className="text-text-soft-400">—</span>;
  const meta = OCCUPANCY_STATUS[status];
  return (
    <Badge
      size="sm"
      type="dot"
      status={BADGE_STATUS[meta.tone]}
      title={meta.hint}
      data-testid={testId}
    >
      {meta.label}
    </Badge>
  );
};

const BAR_TONE = {
  success: 'success',
  warning: 'warning',
  danger: 'error',
  neutral: 'information',
  info: 'information',
} as const;

/** Progress bar coloured by `pctTone`; a value over 100 % fills the bar. */
export const OccupancyBar: React.FC<{
  pct: number | null | undefined;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ pct, label, size = 'sm', className }) => {
  const v = pct ?? 0;
  return (
    <ProgressBar
      size={size}
      value={Math.min(100, Math.max(0, v))}
      tone={BAR_TONE[pctTone(pct ?? null)]}
      aria-label={label}
      aria-valuetext={
        pct === null || pct === undefined ? 'Belum diatur' : fmtPct(pct)
      }
      className={className}
    />
  );
};

/**
 * Status pill of a location / section / bin on the chosen basis (Kapasitas
 * belum diatur · Kosong · Normal · Hampir penuh · Penuh); with the
 * thresholds, its rule is the tooltip (the basis names what to set).
 */
export const ZoneStatusBadge: React.FC<{
  status: ZoneOccupancyStatus;
  thresholds?: ZoneOccupancyThresholds | null;
  basis?: UtilizationBasis;
  testId?: string;
}> = ({ status, thresholds, basis, testId }) => {
  const meta = ZONE_OCCUPANCY_STATUS[status];
  return (
    <Badge
      size="sm"
      type="dot"
      status={BADGE_STATUS[meta.tone]}
      title={thresholds ? meta.hint(thresholds, basis) : undefined}
      data-testid={testId}
    >
      {meta.label}
    </Badge>
  );
};

/** Progress bar of the utilization, coloured by its status. */
export const ZoneBar: React.FC<{
  pct: number | null;
  status: ZoneOccupancyStatus;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ pct, status, label, size = 'sm', className }) => (
  <ProgressBar
    size={size}
    value={Math.min(100, Math.max(0, pct ?? 0))}
    tone={BAR_TONE[ZONE_OCCUPANCY_STATUS[status].tone]}
    aria-label={label}
    aria-valuetext={pct === null ? 'Kapasitas belum diatur' : fmtPct(pct)}
    className={className}
  />
);
